<script lang="ts">
  import { getServices } from '../../app/context';
  import { setComment } from '../../model/ops';
  import type { Category, Kink } from '../../model/types';
  import { LIMITS } from '../../model/types';
  import { format, strings } from '../../strings';
  import Button from '../../ui/Button.svelte';
  import Dialog from '../../ui/Dialog.svelte';

  interface Props {
    /** The kink being commented on; `null` closes the dialog. */
    target: { category: Category; kink: Kink } | null;
    /** The button that opened the dialog. */
    trigger?: HTMLElement | null;
    onclose: () => void;
  }

  let { target, trigger, onclose }: Props = $props();

  const { store, toasts } = getServices();
  const id = $props.id();
  let text = $state('');
  let open = $state(false);

  $effect(() => {
    open = target !== null;
    text = target?.kink.comment ?? '';
  });

  function save(value: string) {
    if (!target) return;
    const kinkId = target.kink.id;
    const had = target.kink.comment !== undefined;
    store.apply((doc) => setComment(doc, kinkId, value));
    open = false;
    if (had && value.trim() === '') {
      toasts.show(strings.commentDeleted, {
        action: { label: strings.undo, run: () => store.undo() },
      });
    }
  }
</script>

<Dialog
  bind:open
  title={target ? format.commentTitle(target.category.name, target.kink.name) : ''}
  returnFocusTo={trigger}
  {onclose}
>
  <form
    id="{id}-form"
    class="flex flex-col gap-1"
    onsubmit={(event) => {
      event.preventDefault();
      save(text);
    }}
  >
    <label for="{id}-text" class="text-sm font-medium">{strings.commentLabel}</label>
    <!-- svelte-ignore a11y_autofocus (the dialog was opened to type into this field) -->
    <textarea
      id="{id}-text"
      bind:value={text}
      maxlength={LIMITS.comment}
      rows="5"
      autofocus
      aria-describedby="{id}-count"
      class="min-h-32 resize-y rounded-lg border border-line-strong/70 bg-surface p-3 text-base text-ink sm:text-sm"
      onkeydown={(event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          save(text);
        }
      }}></textarea>
    <p id="{id}-count" class="self-end text-xs text-ink-muted">
      {format.characters(text.length, LIMITS.comment)}
    </p>
  </form>
  {#snippet footer()}
    {#if target?.kink.comment !== undefined}
      <Button
        variant="ghost"
        class="mr-auto text-danger"
        onclick={() => {
          save('');
        }}
      >
        {strings.delete}
      </Button>
    {/if}
    <Button onclick={() => (open = false)}>{strings.cancel}</Button>
    <Button variant="primary" type="submit" form="{id}-form">{strings.save}</Button>
  {/snippet}
</Dialog>
